import api from "../lib/api";
import { handleError } from "../lib/errors";
import { LIMITS } from "../lib/constants";

export interface ItemAta {
  id: string;
  descricao: string;
  concluido: boolean;
  ata_id: string;
  topico_id?: string;
  entidade?: string;
  prazo?: string;
  ata_origem_numero?: number;
  ata_origem_data?: string;
  numero_item?: string;
  ata?: {
    numero: number;
  };
}

interface BackendItemAta {
  id: number;
  descricao: string;
  concluido: boolean;
  ata_id: number;
  topico_id?: number;
  entidade?: string;
  prazo?: string;
  ata_origem_numero?: number;
  ata_origem_data?: string;
  numero_item?: string;
}

function mapBackendToFrontend(backend: BackendItemAta): ItemAta {
  return {
    id: backend.id.toString(),
    descricao: backend.descricao,
    concluido: backend.concluido,
    ata_id: backend.ata_id.toString(),
    topico_id: backend.topico_id !== null && backend.topico_id !== undefined 
      ? backend.topico_id.toString() 
      : undefined,
    entidade: backend.entidade !== null && backend.entidade !== undefined 
      ? backend.entidade 
      : undefined,
    prazo: backend.prazo !== null && backend.prazo !== undefined 
      ? backend.prazo 
      : undefined,
    ata_origem_numero: backend.ata_origem_numero !== null && backend.ata_origem_numero !== undefined 
      ? backend.ata_origem_numero 
      : undefined,
    ata_origem_data: backend.ata_origem_data !== null && backend.ata_origem_data !== undefined 
      ? backend.ata_origem_data 
      : undefined,
    numero_item: backend.numero_item || undefined,
  };
}

export async function getItemsByAtaId(ataId: string): Promise<ItemAta[]> {
  try {
    const numericId = Number(ataId);
    if (isNaN(numericId)) {
      throw new Error("ID da ata inválido");
    }

    const response = await api.get(`/itens-atas/${numericId}`);
    const backendItems = Array.isArray(response.data) ? response.data : [];
    return backendItems.map(mapBackendToFrontend);
  } catch (error) {
    const message = handleError(error, "getItemsByAtaId");
    throw new Error(message);
  }
}

export async function createItemAta(
  ataId: string,
  input: {
    descricao: string;
    concluido?: boolean;
    topico_id?: string;
    entidade?: string;
    prazo?: string;
    ata_origem_numero?: number;
    ata_origem_data?: string;
  }
): Promise<ItemAta> {
  try {
    const numericId = Number(ataId);
    if (isNaN(numericId)) {
      throw new Error("ID da ata inválido");
    }

    const payload: any = {
      descricao: input.descricao,
      concluido: input.concluido || false,
    };

    // Validação de descrição
    if (!input.descricao || !input.descricao.trim()) {
      throw new Error('Descrição do item é obrigatória. Descreva o que precisa ser feito.');
    }
    if (input.descricao.trim().length > LIMITS.ITEM_DESCRIPTION_MAX) {
      throw new Error(`Descrição excedeu o limite de ${LIMITS.ITEM_DESCRIPTION_MAX} caracteres. Reduza o texto para poder salvar.`);
    }
    if (input.entidade && input.entidade.length > LIMITS.ITEM_ENTITY_MAX) {
      throw new Error(`Entidade/Responsável excedeu o limite de ${LIMITS.ITEM_ENTITY_MAX} caracteres.`);
    }

    if (input.topico_id) {
      payload.topico_id = Number(input.topico_id);
    }
    if (input.entidade) {
      payload.entidade = input.entidade;
    }
    if (input.prazo) {
      payload.prazo = input.prazo;
    }
    if (input.ata_origem_numero !== undefined) {
      payload.ata_origem_numero = input.ata_origem_numero;
    }
    if (input.ata_origem_data) {
      payload.ata_origem_data = input.ata_origem_data;
    }

    const response = await api.post(`/itens-atas/${numericId}`, payload);

    if (!response.data || !response.data.id) {
      throw new Error("Resposta inválida do servidor ao criar item");
    }

    return mapBackendToFrontend(response.data);
  } catch (error) {
    const message = handleError(error, "createItemAta");
    throw new Error(message);
  }
}

export async function updateItemAta(
  itemId: string,
  input: {
    descricao?: string;
    concluido?: boolean;
    topico_id?: string;
    entidade?: string;
    prazo?: string;
  }
): Promise<ItemAta> {
  try {
    const numericId = Number(itemId);
    if (isNaN(numericId)) {
      throw new Error("ID do item inválido");
    }

    const payload: any = {};
    if (input.descricao !== undefined) payload.descricao = input.descricao;
    if (input.concluido !== undefined) payload.concluido = input.concluido;
    if (input.topico_id !== undefined) {
      payload.topico_id = input.topico_id ? Number(input.topico_id) : null;
    }
    if (input.entidade !== undefined) payload.entidade = input.entidade;
    if (input.prazo !== undefined) payload.prazo = input.prazo;

    const response = await api.put(`/itens-atas/${numericId}`, payload);

    if (!response.data || !response.data.id) {
      throw new Error("Resposta inválida do servidor ao atualizar item");
    }

    return mapBackendToFrontend(response.data);
  } catch (error) {
    const message = handleError(error, "updateItemAta");
    throw new Error(message);
  }
}

export async function deleteItemAta(itemId: string): Promise<void> {
  try {
    const numericId = Number(itemId);
    if (isNaN(numericId)) {
      throw new Error("ID do item inválido");
    }

    await api.delete(`/itens-atas/${numericId}`);
  } catch (error) {
    const message = handleError(error, "deleteItemAta");
    throw new Error(message);
  }
}
